// What the delivery-day bars draw: a full calendar from the first to the latest delivered day,
// with the days nothing arrived on left in. Pure: no React, router or Firebase imports, and no
// clock: the axis comes only from the delivered days it is given.
import { addDays, dayRange, daysBetween, isIsoDay } from './dateWindow';
import type { IsoDay } from './dateWindow';

/** The most bars shown at once. */
export const MAX_VISIBLE_DAYS = 60;

/**
 * Bar sizing, tied to the narrowest place the strip renders: the side-panel picker, where the
 * collapse content is a w-80 panel (320) minus p-4 (32) and px-4 (32) on each side = 224 px.
 * 60 bars at BAR_MIN_PX with 59 gaps of BAR_GAP_PX must fit inside NARROW_PANEL_PX.
 */
export const BAR_MIN_PX = 2;
export const BAR_GAP_PX = 1;
export const NARROW_PANEL_PX = 224;

/** Left edge of a tooltip as a percent of its container, centred on the bar and kept inside. */
export function tooltipLeftPct(barCentre: number, containerWidth: number, tipWidth: number): number {
  if (containerWidth <= 0) return 0;
  const max = Math.max(0, containerWidth - tipWidth);
  const left = Math.min(max, Math.max(0, barCentre - tipWidth / 2));
  return (left / containerWidth) * 100;
}

export interface DayBar {
  day: IsoDay;
  delivered: boolean;
}

function cleanDays(dates: readonly IsoDay[]): IsoDay[] {
  return Array.from(new Set(dates.filter(isIsoDay))).sort();
}

/** Full calendar from first to last delivered day. Input is sorted and de-duplicated defensively; invalid days dropped. */
export function calendarAxis(dates: readonly IsoDay[]): DayBar[] {
  const days = cleanDays(dates);
  if (days.length === 0) return [];
  const delivered = new Set(days);
  return dayRange(days[0], days[days.length - 1]).map((day) => ({ day, delivered: delivered.has(day) }));
}

/** Indices into the axis; end is exclusive. */
export interface VisibleWindow {
  start: number;
  end: number;
  hasEarlier: boolean;
  hasLater: boolean;
}

/**
 * requestedEnd: an end index the person paged to, or null. include: an index that must be visible
 * when requestedEnd is null (the selection), or null. A short axis shows everything. requestedEnd is
 * clamped to [min(maxDays, length), length]. With no requestedEnd the view ends at the axis end,
 * unless `include` would fall before its start, in which case the view moves to the page (a multiple of maxDays, from the axis start) that holds it.
 */
export function visibleWindow(
  length: number,
  maxDays: number,
  requestedEnd: number | null,
  include: number | null,
): VisibleWindow {
  if (length <= maxDays) return { start: 0, end: length, hasEarlier: false, hasLater: false };
  let end: number;
  if (requestedEnd !== null) {
    end = Math.min(length, Math.max(maxDays, requestedEnd));
  } else {
    end = length;
    if (include !== null && include < length - maxDays) {
      // Land on a whole page, so the selection sits inside it and not on its edge.
      end = Math.min(length, Math.ceil((include + 1) / maxDays) * maxDays);
    }
  }
  const start = end - maxDays;
  return { start, end, hasEarlier: start > 0, hasLater: end < length };
}

/**
 * The anchor stays put. The other end must be delivered (else the range collapses to the anchor).
 * The pair is ordered. If the span (daysBetween + 1) exceeds maxDays, the far end moves toward the
 * anchor to the furthest delivered day within it. maxDays <= 1 always returns the anchor alone.
 */
export function clampRange(
  dates: readonly IsoDay[],
  anchor: IsoDay,
  other: IsoDay,
  maxDays: number,
): { start: IsoDay; end: IsoDay } {
  const single = { start: anchor, end: anchor };
  if (maxDays <= 1 || anchor === other) return single;
  const days = cleanDays(dates);
  if (!days.includes(other)) return single;
  const forward = other > anchor;
  if (Math.abs(daysBetween(anchor, other)) + 1 <= maxDays) {
    return forward ? { start: anchor, end: other } : { start: other, end: anchor };
  }
  if (forward) {
    const limit = addDays(anchor, maxDays - 1);
    const far = days.filter((d) => d >= anchor && d <= limit).pop() ?? anchor;
    return { start: anchor, end: far };
  }
  const limit = addDays(anchor, -(maxDays - 1));
  const far = days.find((d) => d >= limit && d <= anchor) ?? anchor;
  return { start: far, end: anchor };
}

/** Previous (-1) or next (+1) delivered day after `from`, or null. */
export function stepDelivered(dates: readonly IsoDay[], from: IsoDay, dir: -1 | 1): IsoDay | null {
  const days = cleanDays(dates);
  if (dir === 1) return days.find((d) => d > from) ?? null;
  const before = days.filter((d) => d < from);
  return before.length ? before[before.length - 1] : null;
}
